import { AlsoThereCard } from '@/components/also-there-card';
import { ComparisonResults } from '@/components/comparison-results';
import { ComparisonResultsHeader } from '@/components/comparison-results-header';
import { ComparisonResultsView } from '@/components/comparison-results-view';
import { Footer } from '@/components/footer';
import { FooterCtaSection } from '@/components/footer-cta-section';
import { OnboardingSection } from '@/components/onboarding-section';
import { OnboardingSkeleton } from '@/components/onboarding-skeleton';
import { ScenarioSkeleton } from '@/components/scenario-skeleton';
import { SchedulingModal } from '@/components/scheduling-modal';
import { Text } from '@/components/text';
import { Badge } from '@/components/ui/badge';
import { View } from '@/components/ui/tw-wrappers';
import { SCREEN_MAX_WIDTH } from '@/constants/layout';
import { Colors } from '@/constants/theme';
import { useAnimatedHeader } from '@/hooks/use-animated-header';
import { useCountries } from '@/hooks/useCountries';
import { useTransferPair } from '@/hooks/useTransferPair';
import { useScenarioStore } from '@/stores/scenario-store';
import { Image } from 'expo-image';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { FadeInUp, useAnimatedRef } from 'react-native-reanimated';

export default function CompareScreen() {
  const { t } = useTranslation();
  const onboardingOpen = useScenarioStore(s => s.onboardingOpen);
  const setOnboardingOpen = useScenarioStore(s => s.setOnboardingOpen);
  const [isSchedulingModalVisible, setIsSchedulingModalVisible] = useState(false);

  const { countries, loading: countriesLoading, error: countriesError } = useCountries();

  const inputs = useScenarioStore(s => s.inputs);
  const { data, loading, error } = useTransferPair({
    concept: inputs.invoicingConcept,
    customerId: inputs.customerId,
    vendorId: inputs.vendorId,
    invoicingCurrency: inputs.invoicingCurrency,
  });

  const isConfigComplete =
    !!inputs.customerId &&
    !!inputs.vendorId &&
    inputs.invoicingAmount != null &&
    (inputs.sellThroughLocalPartner ? inputs.partnerMargin != null : true);

  const { headerTranslateY, scrollHandler, totalHeight } = useAnimatedHeader();
  const { height: windowHeight } = useWindowDimensions();

  const scrollViewRef = useAnimatedRef<Animated.ScrollView>();

  // If we land here and there's no scenario, make sure onboarding starts open
  useEffect(() => {
    const isFirstTime = !inputs.customerId && !inputs.vendorId && inputs.invoicingAmount == null;
    if (isFirstTime) {
      setOnboardingOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={styles.mainContainer}>
      {/* BACKGROUND (Fixed) */}
      <View style={[styles.backgroundContainer, { height: windowHeight }]}>
        <Image
          source={require('@/assets/images/background-l.webp')}
          style={styles.backgroundImage}
          contentFit='cover'
          alt={t('common.backgroundAlt', 'Abstract decorative background')}
        />
      </View>

      <Animated.ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
        contentContainerStyle={[styles.scrollContent, { paddingTop: totalHeight }]}>
        {/* ONBOARDING SECTION */}
        {onboardingOpen && (
          <View key='onboarding' style={[styles.onboardingContainer, { minHeight: windowHeight - totalHeight }]}>
            <Animated.View style={styles.descriptionContainer} entering={FadeInUp.delay(200).duration(800).springify()}>
              <Text style={styles.mainTitle} type='defaultBold'>
                {t('onboarding.title')}
              </Text>
              <Text style={styles.onBoardingDescriptionText}>{t('onboarding.subtitle')}</Text>
              <Text style={styles.onBoardingDescriptionText}>{t('onboarding.description')}</Text>
            </Animated.View>
            <Animated.View entering={FadeInUp.delay(400).duration(800).springify()}>
              {countriesLoading ? (
                <OnboardingSkeleton />
              ) : (
                <OnboardingSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                  countriesError={countriesError}
                  onComplete={() => setOnboardingOpen(false)}
                />
              )}
            </Animated.View>
          </View>
        )}

        {/* SUMMARY & RESULTS */}
        {!onboardingOpen && (
          <Animated.View entering={FadeInUp.delay(200).duration(800).springify()} style={styles.resultsContainer}>
            <ComparisonResultsHeader
              countries={countries}
              countriesLoading={countriesLoading}
              data={data}
            />
            <ComparisonResultsView
              data={data}
              countries={countries}
              loading={loading}
              error={error}
              isConfigComplete={isConfigComplete}
              inputs={inputs}
              onSchedulePress={() => setIsSchedulingModalVisible(true)}
            />
          </Animated.View>
        )}
        {data && (
          <FooterCtaSection
            countries={countries}
            data={data}
            loading={loading}
            modelType='compare'
          />
        )}
        <Footer />
      </Animated.ScrollView>
      {data && (
        <SchedulingModal
          isVisible={isSchedulingModalVisible}
          onClose={() => setIsSchedulingModalVisible(false)}
          data={data}
          countries={countries}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: '#E3E3E3',
  },
  scrollView: {
    flex: 1,
    width: '100%',
    backgroundColor: 'transparent',
  },
  scrollContent: {
    flexGrow: 1,
  },
  onboardingContainer: {
    justifyContent: 'flex-start',
    gap: 20,
  },
  resultsContainer: {
    width: '100%',
    alignSelf: 'center',
    flex: 1,
    zIndex: 1,
  },
  backgroundContainer: {
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 0,
  },
  backgroundImage: {
    flex: 1,
    width: '100%',
  },
  descriptionContainer: {
    paddingHorizontal: 40,
    marginTop: 30,
    marginBottom: 10,
  },
  onBoardingDescriptionText: {
    textAlign: 'center',
    color: Colors.light.primary,
    fontSize: 16,
  },
  mainTitle: {
    fontSize: 30,
    textAlign: 'center',
    marginBottom: 15,
  },
});
